import { IconName } from '@flatfile/baseline'
import { format } from 'date-fns'

export interface AppStats {
  appId: string
  environmentId: string
  recordsProcessed: { current: number }
  matchAccuracy: { current: number; previous: number }
  data: { current: number; previous: number }
  uniqueUsers: { current: number; previous: number }
}

export interface StatsUI {
  title: string
  icon: IconName
  value: string | number
  desc?: string
  diff?: number
  tooltip?: string
}

export interface DashboardAnalytics {
  appId: string
  environmentId: string
  date: number
  metricValue: number
  dimensionValue: string
  metric: string
  raw?: any
}

type UserType = 'user' | 'guest'

export type ListMetric = 'fileList' | 'pdvBySpaceList' | 'usersList'

export interface MetricListItem {
  appId: string
  environmentId: string
  createdAt?: string
  extension?: string
  id?: string
  name?: string
  size?: number
  spaceId?: string
  spaceName?: string
  userId?: string
  userType?: UserType
  username?: string
  fileCount?: number
  pdv?: number
  users?: string
  fileSize?: number
  lastUpload?: string
  workbookId?: string
}

export type ListPagination = {
  currentPage: number
  pageCount: number
  totalCount: number
}

export const formatDate = (date?: string, includeTime?: boolean) => {
  if (!date) return ''
  if (includeTime) {
    return format(new Date(date), 'MM/dd/yyyy @ h:mma')
  }
  return format(new Date(date), 'MMM dd, yyyy')
}

const convertStatsResponseToUI = (stats: AppStats): StatsUI[] => {
  const statsUI: StatsUI[] = []
  Object.keys(stats).forEach((key) => {
    switch (key) {
      case 'recordsProcessed':
        statsUI.push({
          title: 'File Records',
          tooltip:
            'File Records Processed refers to the total number of file rows successfully mapped into a Workbook upon file upload. This metric increments with each file uploaded that completes the mapping process. Note: Manual row additions and uploads not completing field mapping are excluded from this count.',
          icon: 'files',
          value: stats[key].current.toLocaleString(),
        })
        break
      case 'matchAccuracy':
        statsUI.push({
          title: 'Match Accuracy',
          tooltip:
            'Match accuracy indicates the percentage of fields accurately auto-mapped by our system. Mappings accepted by end user increase accuracy, while incorrect mappings, including those that should have been ignored, decrease it.',
          icon: 'map-pin-flat',
          value: stats[key].current ? stats[key].current : '--',
          desc: '%',
          diff:
            stats[key].previous > 0
              ? +(((stats[key].current - stats[key].previous) / stats[key].previous) * 100).toFixed(2)
              : stats[key].previous,
        })
        break
      case 'data':
        statsUI.push({
          title: 'File Data',
          tooltip:
            'File Data Size represents the cumulative size of all uploaded files, including those not mapped. This metric accounts for the total volume of data uploaded, irrespective of mapping status.',
          icon: 'file-text',
          value: `${stats[key].current.toLocaleString()}`,
          desc: 'bytes',
          diff:
            stats[key].previous > 0
              ? +(((stats[key].current - stats[key].previous) / stats[key].previous) * 100).toFixed(2)
              : stats[key].previous,
        })
        break
      case 'uniqueUsers':
        statsUI.push({
          title: 'Guests',
          tooltip:
            'Guest Entries track the total count of guests who accessed a Space, regardless of file import activity. For anonymous guests, each session counts as a single entry. If a guest exits and initiates a new session, they are counted anew.',
          icon: 'people',
          value: (stats[key].current || 0).toLocaleString(),
          diff:
            stats[key].previous > 0
              ? +(((stats[key].current - stats[key].previous) / stats[key].previous) * 100).toFixed(2)
              : stats[key].previous,
        })
        break
      default:
        break
    }
  })
  return statsUI
}

export { convertStatsResponseToUI }
